import React, {useEffect, useState} from "react";
import axios from "axios";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";
import DataTable from "react-data-table-component";
import {customStyles} from "../../utils/DataTableCustomStyle";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTriangleExclamation, faTimes, faBoxOpen, faUserCheck, faUsers} from "@fortawesome/free-solid-svg-icons";
import StatCards from "../common/StatCards";

export default function AllocateAsset() {
  const [availableAssets, setAvailableAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assignedAssets, setAssignedAssets] = useState([]);

  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  // Dead Device Warning state
  const [showOfflineWarning, setShowOfflineWarning] = useState(false);
  const [offlineAssetNames, setOfflineAssetNames] = useState([]);

  const fetchAvailableAssets = async () => {
    try {
      const endpoint = `${Constants.API_V1}/${getClientIdOfUser()}/assets/${UrlConstants.GET_ALL_ASSETS}`;
      axios.defaults.headers.common["Authorization"] =
        `${Constants.BEARER} ${getAuthToken()}`;

      const response = await axios.get(`${Constants.BASE_URL}${endpoint}`);
      const allAssets = response?.data?.responseData || [];

      const unassigned = allAssets.filter(
        a => (a.assetStatus || "").toUpperCase() === "ACTIVE"
      );

      setAvailableAssets(unassigned);
    } catch (e) {
      showToast("Error loading assets", { type: "error" });
    }
  };

  const fetchEmployees = async () => {
    try {
      const endpoint = `${Constants.API_V1}/${getClientIdOfUser()}/users/getAllClientUsers`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;

      const response = await axios.get(`${Constants.BASE_URL}${endpoint}`);

      if (response.status === 200 && Array.isArray(response.data.responseData)) {
        setEmployees(response.data.responseData);
      }
    } catch {
      showToast("Error loading employees", { type: "error" });
    }
  };

  const fetchAssignedAssets = async () => {
    if (!selectedEmployeeId) return setAssignedAssets([]);

    try {
      const endpoint = `${Constants.API_V1}/${getClientIdOfUser()}/asset-management/user/${selectedEmployeeId}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;

      const response = await axios.get(`${Constants.BASE_URL}${endpoint}`);
      const raw = Array.isArray(response.data) ? response.data : [];

      const filtered = raw
        .filter(a => !a.returnDate)
        .map((a) => ({
          assignmentId: a.assignmentId,
          assetId: a.asset?.assetId,
          assetName: a.asset?.name,
          assetUnitId: a.asset?.assetUnitId,
        }));

      setAssignedAssets(filtered);
    } catch {}
  };

  useEffect(() => { fetchAvailableAssets(); fetchEmployees(); }, []);
  useEffect(() => { fetchAssignedAssets(); }, [selectedEmployeeId]);

  const proceedWithAllocation = async () => {
    setShowOfflineWarning(false);
    try {
      const payload = {
        requesterId: JSON.parse(localStorage.getItem(Constants.LOGGED_IN_USER))?.userId,
        employeeId: selectedEmployeeId,
        assetIds: selectedAssetIds,
      };

      const endpoint = `${Constants.API_V1}/${getClientIdOfUser()}/asset-management/allocate`;
      const resp = await axios.post(`${Constants.BASE_URL}${endpoint}`, payload);

      if (resp.status === 200) {
        showToast("Assets allocated!", { type: "success" });
        fetchAvailableAssets();
        fetchAssignedAssets();
      }
    } catch {}
  };

  const allocateAssets = async () => {
    if (!selectedEmployeeId || selectedAssetIds.length === 0) {
      return showToast("Select employee & assets", { type: "warning" });
    }

    // Dead Device Warning: check if any selected asset reports as offline
    const offlineOnes = availableAssets.filter(
      a =>
        selectedAssetIds.includes(a.assetId) &&
        (a.networkStatus || a.deviceStatus || "").toUpperCase() === "OFFLINE"
    );

    if (offlineOnes.length > 0) {
      setOfflineAssetNames(
        offlineOnes.map(a => ({
          name: a.name || a.assetUnitId,
          lastSeen: a.lastPingTime || a.lastSeen || null,
        }))
      );
      setShowOfflineWarning(true);
      return;
    }

    await proceedWithAllocation();
  };

  const surrenderAsset = async (assignmentId) => {
    try {
      const endpoint = `${Constants.API_V1}/${getClientIdOfUser()}/asset-management/surrender`;

      const payload = {
        requesterId: JSON.parse(localStorage.getItem(Constants.LOGGED_IN_USER))?.userId,
        assignmentId,
      };

      const resp = await axios.post(`${Constants.BASE_URL}${endpoint}`, payload);

      if (resp.status === 200) {
        showToast("Asset surrendered!", { type: "success" });
        fetchAssignedAssets();
        fetchAvailableAssets();
      }
    } catch {}
  };

  const assetColumns = [
    { name: "ID", selector: row => row.assetId, width: "100px" },
    { name: "Name", selector: row => row.name, wrap: true, grow: 2 },
    { name: "Unit ID", selector: row => row.assetUnitId, width: "150px" },
    {
      name: "Network",
      cell: row => {
        const ns = (row.networkStatus || row.deviceStatus || "").toUpperCase();
        return ns ? (
          <span className={`status-pill ${ns.toLowerCase()}`}>{ns}</span>
        ) : null;
      },
      width: "110px",
    },
  ];

  const assignedColumns = [
    { name: "Assignment ID", selector: row => row.assignmentId, width: "140px" },
    { name: "Asset ID", selector: row => row.assetId, width: "100px" },
    { name: "Asset Name", selector: row => row.assetName, grow: 2 },
    { name: "Unit ID", selector: row => row.assetUnitId, width: "150px" },
    {
      name: "Actions",
      width: "130px",
      cell: (row) => (
        <button className="btn-surrender" onClick={() => surrenderAsset(row.assignmentId)}>
          Surrender
        </button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4 legacy-in-slideover">

      <StatCards
        items={[
          { label: "Available Assets", value: availableAssets.length, icon: faBoxOpen, color: "#10b981", tint: "#e7f8f0" },
          { label: selectedEmployeeId ? "Allocated (selected)" : "Allocated", value: assignedAssets.length, icon: faUserCheck, color: "#FF683A", tint: "#fff1e6" },
          { label: "Employees", value: employees.length, icon: faUsers, color: "#363166", tint: "#eef0fb" },
        ]}
      />

      <div className="bg-white border border-ink-200 rounded-xl shadow-xs p-4">
        <label htmlFor="alloc-employee" className="block text-sm font-medium text-ink-700 mb-1.5">
          Select employee
        </label>
        <select
          id="alloc-employee"
          value={selectedEmployeeId}
          onChange={(e) => setSelectedEmployeeId(e.target.value)}
          className="block w-full sm:max-w-sm h-9 px-3 text-base rounded-md bg-white border border-ink-200 shadow-inset text-ink-900 hover:border-ink-300 focus:border-brand-500 focus:outline-none focus:ring"
        >
          <option value="">Select employee…</option>
          {employees.map((emp) => (
            <option key={emp.userId} value={emp.userId}>
              {emp.fullName || emp.username}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white border border-ink-200 rounded-xl shadow-xs overflow-hidden">
          <div className="px-4 py-3 border-b border-ink-100 flex items-center justify-between">
            <h3 className="text-base font-semibold text-ink-900">Available assets</h3>
            <span className="text-xs text-ink-500 tabular-nums">{selectedAssetIds.length} selected</span>
          </div>
          <div className="allocate-table-wrapper">
            <DataTable
              columns={assetColumns}
              data={availableAssets}
              selectableRows
              onSelectedRowsChange={(s) => setSelectedAssetIds(s.selectedRows.map(r => r.assetId))}
              pagination
              customStyles={customStyles}
            />
          </div>
          <div className="px-4 py-3 border-t border-ink-100 flex justify-end">
            <button
              onClick={allocateAssets}
              disabled={!selectedEmployeeId || selectedAssetIds.length === 0}
              className="inline-flex items-center gap-2 h-9 px-4 text-sm font-medium rounded-md bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Allocate selected
            </button>
          </div>
        </div>

        {selectedEmployeeId && (
          <div className="bg-white border border-ink-200 rounded-xl shadow-xs overflow-hidden">
            <div className="px-4 py-3 border-b border-ink-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-ink-900">Currently allocated</h3>
              <span className="text-xs text-ink-500 tabular-nums">{assignedAssets.length} assets</span>
            </div>
            <div className="allocate-table-wrapper">
              <DataTable
                columns={assignedColumns}
                data={assignedAssets}
                pagination
                customStyles={customStyles}
              />
            </div>
          </div>
        )}
      </div>

      {/* Dead Device Warning Modal */}
      {showOfflineWarning && (
        <div className="modal-backdrop">
          <div className="modal-container" style={{ maxWidth: 460 }}>
            <div
              className="modal-header"
              style={{ background: "#fef3c7", borderBottom: "1px solid #fcd34d" }}
            >
              <h3 style={{ color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
                <FontAwesomeIcon icon={faTriangleExclamation} /> Offline Asset Warning
              </h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowOfflineWarning(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 12, color: "#374151" }}>
                The following selected asset(s) are <strong>Offline</strong> and may not be
                reachable:
              </p>
              <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                {offlineAssetNames.map((a, i) => (
                  <li key={i} style={{ fontSize: 13, marginBottom: 4, color: "#6b7280" }}>
                    <strong>{a.name}</strong>
                    {a.lastSeen && (
                      <span style={{ marginLeft: 8, fontStyle: "italic" }}>
                        — last seen: {new Date(a.lastSeen).toLocaleString()}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <p style={{ color: "#6b7280", fontSize: 13 }}>
                Do you want to assign anyway?
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowOfflineWarning(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                style={{ background: "#d97706", borderColor: "#d97706" }}
                onClick={proceedWithAllocation}
              >
                Assign Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
